
export type Point = { x: number; y: number; id: number };
export type Line = { x1: number; y1: number; x2: number; y2: number; id: number };

const EPS = 1e-6;

const pointKey = (x: number, y: number) => `${x.toFixed(6)},${y.toFixed(6)}`;


export const isClosedRectilinearBoundary = (lines: Line[]): boolean => {
    if (!lines || lines.length < 4) return false;

    const adjacency = new Map<string, Set<string>>();

    for (const line of lines) {
        const dx = line.x2 - line.x1;
        const dy = line.y2 - line.y1;

        const isHorizontal = Math.abs(dy) < EPS && Math.abs(dx) >= EPS;
        const isVertical = Math.abs(dx) < EPS && Math.abs(dy) >= EPS;

        // Rectilinear means every segment must be horizontal or vertical.
        if (!isHorizontal && !isVertical) return false;

        const a = pointKey(line.x1, line.y1);
        const b = pointKey(line.x2, line.y2);
        if (a === b) return false;

        if (!adjacency.has(a)) adjacency.set(a, new Set());
        if (!adjacency.has(b)) adjacency.set(b, new Set());

        adjacency.get(a)!.add(b);
        adjacency.get(b)!.add(a);
    }

    if (adjacency.size < 4) return false;

    // In a single closed polygon loop each vertex has exactly 2 neighbors.
    let hasInvalidDegree = false;
    Array.from(adjacency.values()).forEach((neighbors) => {
        if (neighbors.size !== 2) hasInvalidDegree = true;
    });
    if (hasInvalidDegree) return false;

    const start = adjacency.keys().next().value as string | undefined;
    if (!start) return false;

    const visited = new Set<string>();
    const queue: string[] = [start];

    while (queue.length > 0) {
        const current = queue.shift()!;
        if (visited.has(current)) continue;
        visited.add(current);

        const neighbors = adjacency.get(current);
        if (!neighbors) continue;
        Array.from(neighbors).forEach((n) => {
            if (!visited.has(n)) queue.push(n);
        });
    }

    // All vertices must belong to the same loop.
    return visited.size === adjacency.size;
};

/**
 * Converts a set of connected lines into an ordered polygon boundary.
 * Assumes lines form a single closed loop.
 */
export const convertLinesToBoundary = (lines: Line[]): [number, number][] => {
    if (lines.length === 0) return [];

    // Build adjacency map: point string key -> Set of connected point string keys (deduplicated)
    const adj = new Map<string, Set<string>>();
    const pointMap = new Map<string, { x: number; y: number }>();

    const key = (x: number, y: number) => `${x.toFixed(3)},${y.toFixed(3)}`;

    lines.forEach((l) => {
        const k1 = key(l.x1, l.y1);
        const k2 = key(l.x2, l.y2);
        if (k1 === k2) return; // Skip degenerate zero-length lines

        pointMap.set(k1, { x: l.x1, y: l.y1 });
        pointMap.set(k2, { x: l.x2, y: l.y2 });

        if (!adj.has(k1)) adj.set(k1, new Set());
        if (!adj.has(k2)) adj.set(k2, new Set());

        adj.get(k1)!.add(k2);
        adj.get(k2)!.add(k1);
    });

    if (adj.size < 3) return [];

    // Find the bottom-most (max y), then left-most point as a deterministic start
    let startKey = "";
    let startPt = { x: -Infinity, y: -Infinity };
    pointMap.forEach((pt, k) => {
        if (!adj.has(k)) return;
        if (
            pt.y > startPt.y ||
            (pt.y === startPt.y && pt.x < startPt.x)
        ) {
            startKey = k;
            startPt = pt;
        }
    });
    if (!startKey) return [];

    // Angle-based traversal: always turn in one consistent direction to guarantee
    // a non-self-intersecting boundary. We pick the neighbor with the smallest
    // counter-clockwise angle relative to the incoming direction.

    const path: [number, number][] = [];
    const visitedEdges = new Set<string>();
    const edgeKey = (a: string, b: string) => `${a}|${b}`;

    let curr = startKey;
    // Choose the initial direction: pick the neighbor with the smallest angle from rightward (0)
    // so that we consistently start in a known direction.
    const startNeighbors = Array.from(adj.get(curr)!);
    const currPt = pointMap.get(curr)!;

    // Sort neighbors by angle and pick the one that starts the traversal going right/down
    let prevKey = "";
    {
        let bestAngle = Infinity;
        for (const nk of startNeighbors) {
            const nPt = pointMap.get(nk)!;
            // Angle from the start point to this neighbor
            let a = Math.atan2(nPt.y - currPt.y, nPt.x - currPt.x);
            // Normalize to [0, 2π)
            if (a < 0) a += 2 * Math.PI;
            if (a < bestAngle) {
                bestAngle = a;
                prevKey = nk;
            }
        }
    }

    // Now traverse: at each vertex, given we came from prevKey, pick the next neighbor
    // with the largest clockwise turn (smallest CCW angle relative to incoming direction).
    // This traces the outer boundary consistently.
    let maxSteps = adj.size + 2; // safety limit
    while (maxSteps-- > 0) {
        path.push([pointMap.get(curr)!.x, pointMap.get(curr)!.y]);

        const ek = edgeKey(curr, prevKey);
        visitedEdges.add(ek);

        const neighbors = Array.from(adj.get(curr)!);
        if (neighbors.length === 0) break;

        const fromPt = pointMap.get(prevKey)!;
        const cPt = pointMap.get(curr)!;
        // Incoming angle (direction we came from)
        const incomingAngle = Math.atan2(fromPt.y - cPt.y, fromPt.x - cPt.x);

        // Pick the neighbor with the smallest positive relative angle (most clockwise turn)
        let bestNext = "";
        let bestRelAngle = Infinity;

        for (const nk of neighbors) {
            if (nk === prevKey && neighbors.length > 1) continue; // don't go back unless it's the only option

            const nPt = pointMap.get(nk)!;
            const outAngle = Math.atan2(nPt.y - cPt.y, nPt.x - cPt.x);
            // Relative angle: how far CCW we turn from incoming
            let rel = outAngle - incomingAngle;
            // Normalize to (0, 2π] — exclude 0 to avoid going straight back
            while (rel <= 1e-9) rel += 2 * Math.PI;
            while (rel > 2 * Math.PI + 1e-9) rel -= 2 * Math.PI;

            if (rel < bestRelAngle) {
                bestRelAngle = rel;
                bestNext = nk;
            }
        }

        if (!bestNext) break;

        // Check if we've completed the loop
        if (bestNext === startKey && path.length > 2) {
            break; // Loop closed
        }

        // Check if we've already traversed this edge (infinite loop guard)
        if (visitedEdges.has(edgeKey(bestNext, curr))) {
            break;
        }

        prevKey = curr;
        curr = bestNext;
    }

    return path;
};

/**
 * Checks if two line segments (x1, y1) -> (x2, y2) and (x3, y3) -> (x4, y4) intersect.
 * Handles parallel, collinear, and standard intersections.
 */
export const lineSegmentsIntersect = (
    x1: number, y1: number, x2: number, y2: number,
    x3: number, y3: number, x4: number, y4: number
): boolean => {
    const det = (x2 - x1) * (y4 - y3) - (y2 - y1) * (x4 - x3);
    if (Math.abs(det) < 1e-9) {
        // Parallel or collinear
        const cross = (x3 - x1) * (y2 - y1) - (y3 - y1) * (x2 - x1);
        if (Math.abs(cross) > 1e-9) return false; // Not collinear
        
        const dot = (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1);
        if (dot < 1e-9) return false; // first segment is a point
        
        const t3 = ((x3 - x1) * (x2 - x1) + (y3 - y1) * (y2 - y1)) / dot;
        const t4 = ((x4 - x1) * (x2 - x1) + (y4 - y1) * (y2 - y1)) / dot;
        const minT = Math.min(t3, t4);
        const maxT = Math.max(t3, t4);
        return maxT >= 0 && minT <= 1;
    }

    const t = ((x3 - x1) * (y4 - y3) - (y3 - y1) * (x4 - x3)) / det;
    const u = ((x3 - x1) * (y2 - y1) - (y3 - y1) * (x2 - x1)) / det;

    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
};

